import _ from 'lodash';
import { Action, NgxsOnInit, Selector, State, StateContext, Store } from '@ngxs/store';
import { ApiHttpClient } from 'app/http/clients/api';
import { BorrowerTab } from './borrower-tab';
import { EventBusService } from 'app/modules/shared/services/event-bus.service';
import {
  GlobalBorrowerTabClose,
  GlobalBorrowerTabsCloseAll,
  GlobalBorrowerTabsInactive,
  GlobalBorrowerTabsPatch,
  GlobalBorrowerTabUpdate } from './global-borrower-tabs.actions';
import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { append, patch, removeItem, updateItem } from '@ngxs/store/operators';

export class GlobalBorrowerTabsStateModel {
  tabs: any;
  activeTab: null | BorrowerTab;
}

const TAB_LIMIT = 15;

@State<GlobalBorrowerTabsStateModel>({
  name: 'globalBorrowerTabs',
  defaults: {
    tabs: [],
    activeTab: null
  }
})
@Injectable()
export class GlobalBorrowerTabsState implements NgxsOnInit {

  @Selector()
  static tabs(state: GlobalBorrowerTabsStateModel) {
    return state.tabs;
  }

  @Selector()
  static activeTab(state: GlobalBorrowerTabsStateModel) {
    return state.activeTab;
  }

  constructor(
    private eventBusService: EventBusService,
    private store: Store,
    private http: ApiHttpClient,
    private router: Router,
    private location: Location
  ) {
  }

  ngxsOnInit() {
    const tabs = JSON.parse(localStorage.getItem('borrowerTabs') || '[]');
    this.store.dispatch(new GlobalBorrowerTabsPatch(tabs));

    this.eventBusService.subscribe(this.eventBusService.types.LocationChangedEvent).subscribe(() => {
      const path = this.location.path();
      // ignore borrower paths since those are handled by BorrowerOpenedEvent
      if (/^\/borrower/.test(path)) {
        return;
      }
      this.store.dispatch(new GlobalBorrowerTabsInactive());
    });
  }

  @Action(GlobalBorrowerTabsPatch)
  globalBorrowerTabsPatch({ patchState }: StateContext<GlobalBorrowerTabsStateModel>, { tabs }: GlobalBorrowerTabsPatch) {
    patchState({ tabs: tabs.slice(-TAB_LIMIT) });
  }

  @Action(GlobalBorrowerTabUpdate)
  globalBorrowerTabUpdate(
    context: StateContext<GlobalBorrowerTabsStateModel>,
    { tab }: GlobalBorrowerTabUpdate
  ) {
    const existingTab = context.getState().tabs.find(x => x.tabId === tab.tabId);

    context.patchState({ activeTab: tab });

    if (!existingTab) { // append tab
      context.setState(patch({
        tabs: append<BorrowerTab>([tab])
      }));
    } else { // update tab
      if (_.isEqual(existingTab, tab)) {
        return; // nothing to do
      }
      context.setState(patch({
        tabs: updateItem<BorrowerTab>(x => x?.tabId === tab.tabId, tab)
      }));
    }

    // we know something changed - save tabs
    this.saveTabs(context);
  }

  @Action(GlobalBorrowerTabsInactive)
  globalBorrowerTabsInactive(
    { patchState }: StateContext<GlobalBorrowerTabsStateModel>,
    { }: GlobalBorrowerTabsInactive
  ) {
    patchState({ activeTab: null });
  }

  @Action(GlobalBorrowerTabClose)
  globalBorrowerTabClose(
    context: StateContext<GlobalBorrowerTabsStateModel>,
    { tabId }: GlobalBorrowerTabClose
  ) {
    // capture "before" state
    const activeIndex = context.getState().tabs.findIndex(x => x.tabId === tabId);
    const isActiveTab = context.getState().activeTab?.tabId === tabId;

    // remove tab from state
    context.setState(patch({
        tabs: removeItem<BorrowerTab>(tab => tab?.tabId === tabId)
    }));

    this.saveTabs(context);

    // reset active tab
    if (isActiveTab) {
      const tabs = context.getState().tabs;
      const activeTab: BorrowerTab | null = tabs[activeIndex] || tabs[tabs.length - 1] || null;
      context.patchState({ activeTab });
      this.router.navigateByUrl(activeTab ? `/borrower/${activeTab.tabId}` : '/');
    }
  }

  @Action(GlobalBorrowerTabsCloseAll)
  globalBorrowerTabsCloseAll(
    context: StateContext<GlobalBorrowerTabsStateModel>,
    { }: GlobalBorrowerTabsCloseAll
  ) {
    const activeTab = context.getState().activeTab;

    context.patchState({
      tabs: [],
      activeTab: null
    });

    this.saveTabs(context);

    if (activeTab) {
      this.router.navigate(['/']);
    }
  }

  private saveTabs({ getState }: StateContext<GlobalBorrowerTabsStateModel>): void {
    const tabs = JSON.stringify(getState().tabs);
    localStorage.setItem('borrowerTabs', tabs);
  }
}
